import { VerdictPreview } from '@/components/VerdictPreview';
import { useTranslation } from 'react-i18next';

// The three stages, in the order the product runs them. Titles only — the
// landing page already carries the sentences, and this plane is read in the
// seconds it takes the form column to be filled in.
const STAGES: { no: string; title: string }[] = [
  { no: '01', title: 'welcome.step_collect_title' },
  { no: '02', title: 'welcome.step_analyse_title' },
  { no: '03', title: 'welcome.step_verdict_title' },
];

/**
 * The sign-in shell's product plane — the second column behind
 * `/login` and `/pending`.
 *
 * The reference layout puts a dashboard screenshot here. A screenshot would be
 * a picture of a page the reader is about to sign in to see, and it goes stale
 * the first time a column moves; the payoff of this product is one analysis
 * result, so the plane carries the real fixture instead
 * (`VerdictPreview`, the same card the landing hero shows) and nothing else
 * that could be read as a claim about a customer's data. Styles are `.auth-*`.
 *
 * The plane is read as three bands rather than one floating cluster: the claim
 * at the top, the artifact it is a claim about in the middle, the pipeline it
 * came out of along the bottom — the same top/middle/bottom shape the form
 * column across the hairline has (lockup, form, small print).
 */
export function AuthBrandPanel() {
  const { t, i18n } = useTranslation();
  // Negative tracking tightens Latin caps; on CJK glyphs it just crowds them —
  // the same rule the landing page's eyebrow follows.
  // `?? ''`: a unit test renders this without an i18n instance, where i18next
  // hands back a stub and `language` is undefined.
  const isCjk = (i18n.language ?? '').startsWith('zh');

  return (
    <div className="auth-brand">
      <div className="auth-brand-inner">
        <div className="auth-brand-head">
          <p className="auth-brand-eyebrow" style={{ letterSpacing: isCjk ? '0.04em' : '0.11em' }}>
            {t('welcome.eyebrow')}
          </p>
          <p className="auth-brand-lede">{t('welcome.lede')}</p>
        </div>

        <div className="auth-brand-card">
          <VerdictPreview />
        </div>

        {/* The pipeline the card came out of, as a rail: three equal cells off
            one rule, so the strip closes the plane instead of trailing off it. */}
        <ol className="auth-brand-stages">
          {STAGES.map((stage) => (
            <li key={stage.no}>
              <span className="auth-brand-stage-no">{stage.no}</span>
              {t(stage.title)}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
